import { useCallback, useEffect, useState } from "react";

import { daysCharged, fmtISODate, fmtISORange, occupationLabel, sickPerYear } from "@core";
import type { DocStatus } from "@core";

import type { EmployerView } from "../lib/employer";
import { loadPerson, openDocument, type PersonFile, type StoredDoc } from "../lib/person";

/**
 * One person's file.
 *
 * The register says what they worked and the payslip says what it comes to.
 * This is everything else an employer gets asked for at a desk: what they are
 * on, what they were on before, whether their papers are current, and how much
 * leave is left.
 *
 * Read-only. The one thing on this page that would rewrite what somebody is
 * paid — changing terms — stays behind the app's deliberate sheet rather than
 * being slipped in among a page of details.
 */

const DOC_TONE: Record<DocStatus, string> = {
  ok: "ok", expiring: "warn", expired: "warn", missing: "warn",
};
const DOC_WORD: Record<DocStatus, string> = {
  ok: "On file", expiring: "Expiring", expired: "Expired", missing: "Missing",
};

const LEAVE_TONE: Record<string, string> = {
  approved: "ok", pending: "warn", declined: "off",
};

function when(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  }).format(new Date(iso));
}

export function Person({ ctx, employeeId, onBack }: {
  ctx: EmployerView;
  employeeId: string;
  onBack: () => void;
}) {
  const [file, setFile] = useState<PersonFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    loadPerson(employeeId, ctx.country as any).then((f) => {
      if (cancelled) return;
      setFile(f);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [employeeId, ctx.country]);

  useEffect(load, [load]);

  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency", currency: ctx.currency, maximumFractionDigits: 2,
  });

  const firstName = file?.employee.fullName.split(" ")[0] ?? "them";

  async function open(doc: StoredDoc) {
    setOpening(doc.id);
    setProblem(null);
    const r = await openDocument(doc);
    setOpening(null);
    if (!r.ok) {
      // Two different failures, and saying the wrong one would be a small lie
      // about a record somebody is entitled to trust.
      setProblem(r.reason === "UNREADABLE"
        ? "That file could not be reached, so nothing was shown and nothing was recorded. " +
          "It may have been removed from storage."
        : "The file is there, but opening it could not be recorded — and this household " +
          `promises ${firstName} a record of who looks. It was not opened.`);
      return;
    }
    window.open(r.url, "_blank", "noopener,noreferrer");
    // Reloaded so the access log below shows the entry that was just written.
    load();
  }

  if (loading) return <div className="centered">Reading the file…</div>;
  if (!file) {
    return (
      <div className="panel">
        <div className="empty">
          That person could not be read.
          <br />
          <button className="btn inline quiet" type="button" onClick={onBack}>Back to People</button>
        </div>
      </div>
    );
  }

  const { employee, current, terms } = file;
  const left = current ? current.annualLeaveDays - file.leaveTaken : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <button className="btn inline quiet" type="button" onClick={onBack}>← People</button>
          <h1 style={{ marginTop: 10 }}>{employee.fullName}</h1>
          <p>
            {occupationLabel(employee.occupation, employee.occupationOther)}
            {file.siteName ? ` · ${file.siteName}` : ""} · since {fmtISODate(employee.startedOn)}
            {employee.status === "ended" ? " · employment ended" : ""}
          </p>
        </div>
      </div>

      {problem ? <div className="notice error">{problem}</div> : null}

      {/* ---------- what they are on ---------- */}
      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">
            Current terms{current ? ` · from ${fmtISODate(current.validFrom)}` : ""}
          </div>
        </div>
        {!current ? (
          <div className="notice warn" style={{ margin: 22 }}>
            This person has no current terms, so they cannot clock in — a shift that cannot be
            priced is refused at the tap. Add terms in the app before they next work.
          </div>
        ) : (
          <div className="panel-body">
            <div className="kpis">
              <div className="kpi"><small>Rate an hour</small><strong>{money.format(current.rate)}</strong></div>
              <div className="kpi"><small>Ordinary day</small><strong>{current.ordinaryDaily}h</strong></div>
              <div className="kpi"><small>Ordinary week</small><strong>{current.ordinaryWeekly}h</strong></div>
              <div className="kpi"><small>Days a week</small><strong>{current.daysPerWeek}</strong></div>
            </div>
            <table className="rows" style={{ marginTop: 6 }}>
              <tbody>
                <Row label="Overtime" value={`${current.ot}× · ${money.format(current.rate * current.ot)} an hour`} />
                <Row label="Sunday" value={`${current.sunday}× · ${money.format(current.rate * current.sunday)} an hour`} />
                <Row
                  label="Public holiday"
                  value={`${current.holiday}× a day's wage`}
                  note="BCEA s18 prices the whole day, not the hours — Shifty pays the greater of double the day and a day plus what was earned."
                />
                <Row label="Break" value={`${current.breakMins} min, only after ${current.breakAfter}h`} />
                <Row label="Annual leave" value={`${current.annualLeaveDays} days`} />
                <Row
                  label="Sick leave"
                  value={`${sickPerYear(current.sickLeaveDays, current.sickCycleMonths)} a year`}
                  note={`${current.sickLeaveDays} days per ${current.sickCycleMonths}-month cycle.`}
                />
                <Row label="Family responsibility" value={`${current.familyLeaveDays} days`} />
              </tbody>
            </table>
          </div>
        )}
        <div className="panel-foot">
          <span className="panel-note">
            Terms are changed in the app. A rise there never restates what was already paid —
            every shift keeps the terms it was worked under.
          </span>
        </div>
      </div>

      {/* ---------- what they were on before ---------- */}
      {terms.filter((t) => t.validTo !== null).length > 0 ? (
        <div className="panel">
          <div className="panel-top"><div className="panel-title">What came before</div></div>
          <table className="rows">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Rate</th>
                <th>In force</th>
                <th style={{ paddingRight: 22 }}>Ordinary day</th>
              </tr>
            </thead>
            <tbody>
              {terms.filter((t) => t.validTo !== null).map((t) => (
                <tr key={t.id}>
                  <td style={{ paddingLeft: 22 }} className="num">{money.format(t.rate)} an hour</td>
                  <td>{fmtISORange(t.validFrom, t.validTo!)}</td>
                  <td style={{ paddingRight: 22 }} className="num">{t.ordinaryDaily}h</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="panel-foot">
            <span className="panel-note">
              The evidence that a rise did not rewrite the past. Shifts worked under these keep them.
            </span>
          </div>
        </div>
      ) : null}

      {/* ---------- papers ---------- */}
      <div className="panel">
        <div className="panel-top"><div className="panel-title">Documents</div></div>
        <table className="rows">
          <thead>
            <tr>
              <th style={{ paddingLeft: 22 }}>Document</th>
              <th>Filed</th>
              <th>Expires</th>
              <th />
              <th style={{ paddingRight: 22 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {file.documents.map(({ kind, status }) => {
              const stored = file.docs.find((d) => d.kind === kind);
              return (
                <tr key={kind}>
                  <td style={{ paddingLeft: 22 }}>
                    {kind}
                    {stored ? <div className="panel-note">{stored.fileName}</div> : null}
                  </td>
                  <td className="num">{stored ? when(stored.uploadedAt) : "—"}</td>
                  <td className="num">{stored?.validUntil ? when(stored.validUntil) : "—"}</td>
                  <td>
                    {stored ? (
                      <button
                        className="btn inline quiet" type="button"
                        disabled={opening === stored.id}
                        onClick={() => open(stored)}
                      >
                        {opening === stored.id ? "Opening…" : "Open"}
                      </button>
                    ) : null}
                  </td>
                  <td style={{ paddingRight: 22 }}>
                    <span className={`pill ${DOC_TONE[status]}`}>{DOC_WORD[status]}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="panel-foot">
          <span className="panel-note">
            Opening one is recorded, and {employee.fullName.split(" ")[0]} can see that you did.
            A missing document never stops somebody clocking in.
          </span>
        </div>
      </div>

      {/* POPIA: the worker is promised they can see who opened their papers.
          The employer sees the same list, so nothing about it is a surprise. */}
      {file.docs.length > 0 ? (
        <div className="panel">
          <div className="panel-top"><div className="panel-title">Who has opened them</div></div>
          {file.views.length === 0 ? (
            <div className="empty">Nobody has opened these yet.</div>
          ) : (
            <table className="rows">
              <tbody>
                {file.views.map((v, i) => {
                  const doc = file.docs.find((d) => d.id === v.documentId);
                  return (
                    <tr key={`${v.at}-${i}`}>
                      <td style={{ paddingLeft: 22 }}>{doc?.kind ?? "A document"}</td>
                      <td className="num">{v.action}</td>
                      <td style={{ paddingRight: 22 }} className="num">
                        {new Intl.DateTimeFormat("en-GB", {
                          timeZone: ctx.timezone, day: "numeric", month: "short",
                          year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
                        }).format(new Date(v.at))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {/* ---------- leave ---------- */}
      <div className="panel">
        <div className="panel-top"><div className="panel-title">Leave</div></div>
        <div className="panel-body">
          <div className="kpis">
            <div className="kpi">
              <small>Annual left</small>
              <strong>{current ? `${left} of ${current.annualLeaveDays}` : "—"}</strong>
            </div>
            <div className="kpi"><small>Taken</small><strong>{file.leaveTaken} days</strong></div>
            <div className="kpi">
              <small>Waiting on you</small>
              <strong>{file.leave.filter((l) => l.status === "pending").length}</strong>
            </div>
          </div>
        </div>
        {file.leave.length === 0 ? (
          <div className="empty">No leave has been asked for.</div>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Dates</th>
                <th>Type</th>
                <th>Days</th>
                <th style={{ paddingRight: 22 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {file.leave.map((l) => (
                <tr key={l.id}>
                  <td style={{ paddingLeft: 22 }} className="num">
                    {fmtISORange(l.startsOn, l.endsOn)}
                    {l.reason ? <div className="panel-note">{l.reason}</div> : null}
                  </td>
                  <td style={{ textTransform: "capitalize" }}>{l.type}</td>
                  <td className="num">
                    {daysCharged(l)}
                    {/* Both numbers, when they differ. The worker is entitled to
                        see that the charge was changed and why. */}
                    {l.daysCharged != null && l.daysCharged !== l.days ? (
                      <div className="panel-note">
                        Shifty counted {l.days}
                        {l.daysNote ? ` · ${l.daysNote}` : ""}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ paddingRight: 22 }}>
                    <span className={`pill ${LEAVE_TONE[l.status] ?? "off"}`}>{l.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="panel-foot">
          <span className="panel-note">
            Only approved days count against the balance — a request nobody has answered has not
            been granted, and counting it would tell somebody they have less leave than they do.
            Anything waiting is answered under Waiting on you.
          </span>
        </div>
      </div>
    </>
  );
}

function Row({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <tr>
      <td style={{ paddingLeft: 22 }}>
        {label}
        {note ? <div className="panel-note">{note}</div> : null}
      </td>
      <td style={{ paddingRight: 22, textAlign: "right" }} className="num">{value}</td>
    </tr>
  );
}
